import { z } from "zod";

/**
 * Ordem do menu lateral escolhida pela pessoa — shape de `User.sidebarOrder`.
 *
 * Guarda chaves, não o menu: `grupos` são as chaves dos grupos e `itens` mapeia cada grupo para
 * os `href` dos seus links. Assim o que a pessoa arrastou continua valendo quando um item ganha
 * rótulo novo, ícone novo ou muda de permissão.
 *
 * Chave que sumiu do código é ignorada na leitura e item que o código ganhou entra na posição
 * padrão, então mexer no menu nunca invalida a preferência de ninguém.
 */
export const ordemDoMenuSchema = z.object({
  grupos: z.array(z.string().min(1).max(64)).max(40).default([]),
  itens: z
    .record(z.string().min(1).max(64), z.array(z.string().min(1).max(200)).max(60))
    .default({}),
});
export type OrdemDoMenu = z.infer<typeof ordemDoMenuSchema>;

/** `null` volta o menu pra ordem do código. */
export const salvarOrdemDoMenuSchema = ordemDoMenuSchema.nullable();

/**
 * Reordena `lista` conforme as chaves salvas, conciliando com o código.
 *
 * Chave salva que não existe mais é descartada. Entrada nova — item que o produto ganhou depois
 * que a pessoa arrastou — não vai parar no fim: entra no índice que tem no código, que é onde quem
 * desenhou o menu a colocou. Sem isso, toda tela nova nasceria no rodapé do menu pra quem já tinha
 * preferência salva, e ninguém acharia.
 */
export function reconciliarOrdem<T>(
  lista: T[],
  chaveDe: (item: T) => string,
  ordem: string[] | undefined,
): T[] {
  if (!ordem?.length) return lista;
  const posicao = new Map(ordem.map((chave, i) => [chave, i]));
  const salvos = lista
    .filter((item) => posicao.has(chaveDe(item)))
    .sort((a, b) => posicao.get(chaveDe(a))! - posicao.get(chaveDe(b))!);
  const saida = [...salvos];
  lista.forEach((item, indiceNoCodigo) => {
    if (!posicao.has(chaveDe(item))) saida.splice(indiceNoCodigo, 0, item);
  });
  return saida;
}
